import { uploadFileWithProvidedPath, useStorage } from "../hooks/useStorage";
import { useDatabaseFunctionsWithPromise } from "../hooks/useDatabase";
import {
	FileWithShare,
	SupabaseTableEnum,
	SupabaseShareEntityViewEnum,
} from "@/lib/supabase/supabaseTypes";
import { StorageBucketsEnum } from "../types/enums";
import { v4 } from "uuid";
import { useCentralStore } from "../store/Central";
import { Logger } from "@/lib/logger/Logger";
import { showNotification } from "../store/Central/selectors";
import { useState } from "react";
import { supabase } from "@/lib/supabase";

export const useUploadPatientFiles = () => {
	const { insertDataWithPromise } = useDatabaseFunctionsWithPromise();
	const { organization, connectRelationshipId } = useCentralStore(
		(state) => ({
			organization: state.organization,
			connectRelationshipId: state.connectRelationshipId,
		})
	);
	const [patientFilesLookup, setPatientFilesLookup] = useState<
		Record<string, FileWithShare>
	>({});
	const uploadFile = async (file: File, patientId: string) => {
		const filePath = `${v4()}-${file.name}`;
		const { success, pathName } = await uploadFileWithProvidedPath({
			fileBody: file,
			filePath: filePath,
		});

		if (!success) {
			showNotification({
				message: "Fehler beim Hochladen der Datei für den Patienten",
				type: "error",
			});
			Logger.error("Error uploading file for patient");
			return;
		}
		const { error, data } = await insertDataWithPromise(
			SupabaseTableEnum.FILES,
			[
				{
					file_name: file.name,
					bucket_name: StorageBucketsEnum.V1,
					path_name: pathName,
					patient_id: patientId,
					organization_id: organization?.id,
				},
			]
		);

		if (error) {
			Logger.error(error, {}, "Error inserting file for patient");
			return;
		}

		updateFile(data?.[0]);
	};

	const fetchFiles = async (patientId: string) => {
		setPatientFilesLookup({});
		let filter = `organization_id.eq.${organization?.id}`;

		if (connectRelationshipId) {
			filter += `,connect_relationship_id.eq.${connectRelationshipId}`;
		}

		const { data, error } = await supabase
			.from(SupabaseShareEntityViewEnum.FILES_WITH_SHARES)
			.select()
			.eq("patient_id", patientId)
			.or(filter);

		if (error) {
			showNotification({
				message: "Fehler beim Abrufen der Dateien für den Patienten",
				type: "error",
			});
			Logger.error(error, {}, "Error fetching files for patient");
			return;
		}
		if (data?.length) {
			const filesLookup = data?.reduce(
				(acc: Record<string, FileWithShare>, row: FileWithShare) => {
					acc[row.id as string] = row;
					return acc;
				},
				{}
			);

			setPatientFilesLookup(filesLookup);
		}
	};

	const updateFile = (file: FileWithShare) => {
		setPatientFilesLookup((prev) => ({
			...prev,
			[file.id as string]: file,
		}));
	};

	return {
		uploadFile,
		files: Object.values(patientFilesLookup),
		fetchFiles,
		updateFile,
	};
};
